import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import LangToggle from "../components/LangToggle";
import { Briefcase, User, UserCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AccountTypePicker() {
    const { t, lang, toggle } = useLang();
    const navigate = useNavigate();
    const [picked, setPicked] = useState("");

    const submit = (e) => {
        e.preventDefault();
        if (!picked) {
            toast.error(t.accountTypeRequired);
            return;
        }
        if (picked === "employee") {
            navigate("/register/employee");
        } else {
            navigate(`/register?type=${picked}`);
        }
    };

    const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-8 sm:px-6 sm:py-12">
                <header className="mb-6 flex items-center justify-between sm:mb-10">
                    <Link
                        to="/login"
                        className="font-heading text-xl font-bold text-neutral-900 sm:text-2xl"
                        data-testid="acct-type-brand"
                    >
                        معيار
                    </Link>
                    <LangToggle lang={lang} onToggle={toggle} t={t} />
                </header>

                <div className="mb-8 text-center sm:mb-10">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                        {t.accountTypeTitle}
                    </p>
                    <h1 className="mt-1 font-heading text-3xl font-bold sm:text-4xl lg:text-5xl">
                        {t.accountTypePickerTitle}
                    </h1>
                    <p className="mx-auto mt-2 max-w-xl text-sm text-neutral-500 sm:text-base">
                        {t.accountTypePickerSubtitle}
                    </p>
                </div>

                <form onSubmit={submit} className="flex-1">
                    <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-3">
                        <Card
                            icon={Briefcase}
                            title={t.accountTypeManager}
                            desc={t.accountTypeManagerDesc}
                            active={picked === "manager"}
                            onClick={() => setPicked("manager")}
                            testId="acct-type-manager"
                            tint="from-amber-50 to-amber-100/40"
                        />
                        <Card
                            icon={UserCheck}
                            title={t.accountTypeEmployee}
                            desc={t.accountTypeEmployeeDesc}
                            active={picked === "employee"}
                            onClick={() => setPicked("employee")}
                            testId="acct-type-employee"
                            tint="from-sky-50 to-sky-100/40"
                        />
                        <Card
                            icon={User}
                            title={t.accountTypeIndividual}
                            desc={t.accountTypeIndividualDesc}
                            active={picked === "individual"}
                            onClick={() => setPicked("individual")}
                            testId="acct-type-individual"
                            tint="from-emerald-50 to-emerald-100/40"
                        />
                    </div>

                    <div className="mt-8 flex flex-col items-center gap-3">
                        <Button
                            type="submit"
                            disabled={!picked}
                            className="h-12 gap-2 bg-neutral-900 px-8 text-white hover:bg-neutral-800"
                            data-testid="acct-type-continue"
                        >
                            {t.accountTypeContinue}
                            <Arrow className="h-4 w-4" />
                        </Button>
                        <Link to="/login" className="text-sm text-neutral-500 hover:text-neutral-900">
                            {t.haveAccount} {t.loginLink}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Card({ icon: Icon, title, desc, active, onClick, testId, tint }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            className={`group flex h-full flex-col items-start gap-3 rounded-2xl border-2 bg-gradient-to-br ${tint} p-5 text-start transition sm:p-6 ${
                active
                    ? "border-amber-600 shadow-lg ring-4 ring-amber-200/50"
                    : "border-neutral-200 hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md"
            }`}
        >
            <div
                className={`flex h-12 w-12 items-center justify-center rounded-xl ring-1 transition ${
                    active
                        ? "bg-amber-600 text-white ring-amber-700"
                        : "bg-white text-amber-700 ring-amber-200 group-hover:bg-amber-600 group-hover:text-white"
                }`}
            >
                <Icon className="h-6 w-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-neutral-900">{title}</h3>
            <p className="text-sm leading-relaxed text-neutral-600">{desc}</p>
            {active ? (
                <span className="mt-auto inline-flex items-center gap-1 rounded-full bg-amber-600 px-3 py-1 text-xs font-bold text-white">
                    ✓
                </span>
            ) : null}
        </button>
    );
}
